/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import ApiV2 from '@/adapters/api-v2';
import {
  BrandKitAssets,
  useGetScopedBrandKit,
  useUploadScopedBrandKitAsset,
} from '@/adapters/assets';
import { useGetProject, useGetProjectVideoTranscript } from '@/adapters/projects';
import { runQuoteDetection } from '@/adapters/quote-detection';
import { generateSegmentFragments } from '@/adapters/generate-segments';
import { useGetUserAssignedSegments, useInviteSegmentSpeakers } from '@/adapters/speakers';
import { useGetFragments } from '@/adapters/workbentch';
import backArrowIcon from '@/assets/icons/back-arrow.svg';
import customizeIcon from '@/assets/icons/customize.svg';
import quoteIcon from '@/assets/icons/quote.svg';
import runsheetIcon from '@/assets/icons/runsheet.svg';
import segmentsIcon from '@/assets/icons/segments.svg';
import BrandKitVideoItem from '@/components/BrandKitVideoItem';
import DownloadButton from '@/components/common/DownloadButton';
import { FileUpload } from '@/components/FileUpload';
import Loading from '@/components/Loading';
import PopoutWindow from '@/components/PopoutWindow/PopoutWindow';
import RunsheetSelector from '@/components/RunsheetSelector';
import GenerateSegmentsModal from '@/components/modals/GenerateSegmentsModal';
import AVDisplay from '@/components/Videoplayer/AVDisplay';
import SimpleHlsPlayer from '@/components/Videoplayer/SimpleHlsPlayer';
import WorkbenchTranscript from '@/components/Videoplayer/WorkbenchTranscript';
import { useSocketIo } from '@/contexts/SocketIoContext';
import { UserContext } from '@/contexts/UserContext';
import { WorkbenchContext } from '@/contexts/WorkbenchContext';
import { useAudioLoader } from '@/hooks/useAudioLoader';
import { useFragments } from '@/hooks/useFragments';
import { LastSegmentEndTimeProvider } from '@/hooks/useLastSegmentEndTime';
import { PROJECTS_URL } from '@/routePaths';
import { TranscriptItem as TranscriptItemModel } from '@/types/transcript';
import type { ApiFragment } from '@/types/workbench';
import { FragmentStatus, WorkerStatus } from '@/types/workbench';
import { convertS3PathToHttpUrl } from '@/utils/s3Utils';
import { Box, CircularProgress, List, ListItem, ListItemText } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import styles from './Workbench.module.scss';

type BrandOptionKey = 'intro' | 'outro' | 'logo';
interface BrandKitPreviewAsset {
  path: string | null;
  s3Path: string | null;
}

// import PrimaryButton from '@/components/common/PrimaryButton';

/**
 * Polls an S3 URL until the file is available, then shows the download button.
 * @reason Shows processing status in Version History until the exported file is ready.
 */
const PollingDownloadButton: FC<{
  url: string;
  fileName: string;
  iconOnly?: boolean;
}> = ({ url, fileName, iconOnly }) => {
  const [isReady, setIsReady] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let mounted = true;

    const checkAvailability = async (): Promise<void> => {
      try {
        const controller = new AbortController();
        const response = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
          signal: controller.signal,
        });
        controller.abort();
        if (mounted && (response.ok || response.status === 206)) {
          setIsReady(true);
          setIsChecking(false);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch {
        // File not ready yet, continue polling
      }
    };

    // Check immediately
    void checkAvailability();

    // Then poll every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      void checkAvailability();
    }, 3000);

    return () => {
      mounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [url]);

  if (isReady) {
    return <DownloadButton url={url} fileName={fileName} size="small" iconOnly={iconOnly} />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CircularProgress size={16} />
      <span style={{ fontSize: 12, color: '#666' }}>
        {isChecking ? 'Processing...' : 'Processing...'}
      </span>
    </Box>
  );
};

const Workbench: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { data: project, isLoading, refetch } = useGetProject(projectId as string);
  const [videoUrl, setVideoUrl] = useState('');
  const [s3Key, setS3Key] = useState('');
  const [transcriptItems, setTranscriptItems] = useState<TranscriptItemModel[]>([]);
  const [lastSegmentEndTimeMs, setLastSegmentEndTimeMs] = useState<number>(0);
  const { userState } = useContext(UserContext);
  const { fragments } = useContext(WorkbenchContext);
  const { user: auth0User } = useAuth0();
  const isSpeakerRole = userState?.role === 'user';
  const { data: assignedSegmentsData } = useGetUserAssignedSegments(projectId);
  // Speakers don't get transcript_response socket events (we skip the worker
  // subscription entirely for them), so pull the segment-sliced transcript
  // from eos-app instead. Hook is disabled for editors because their
  // transcript flows through the socket.
  const { data: speakerTranscript, refetch: refetchSpeakerTranscript } =
    useGetProjectVideoTranscript(
      isSpeakerRole ? (projectId as string) : '',
      isSpeakerRole ? 'original' : '',
    );

  useEffect(() => {
    if (!isSpeakerRole) return;
    if (!projectId) return;
    void refetchSpeakerTranscript();
  }, [isSpeakerRole, projectId, refetchSpeakerTranscript]);
  const inviteSegmentSpeakers = useInviteSegmentSpeakers();
  const { enqueueSnackbar } = useSnackbar();
  // Keyed off the clicked overlay-history row's timestamp so only that row
  // shows a spinner — every entry in the list shares the same segmentId, so
  // keying off segmentId would spin all of them at once.
  const [pendingInviteTimestamp, setPendingInviteTimestamp] = useState<number | null>(null);
  const [showRunsheetSelector, setShowRunsheetSelector] = useState(false);
  const {
    EXTERNAL_CALLBACK_onNewFragments,
    EXTERNAL_CALLBACK_onFragmentStatus,
    handleFragmentsRebuild,
  } = useFragments(isSpeakerRole ? auth0User?.sub : undefined);
  const [isQuoteDetecting, setIsQuoteDetecting] = useState(false);
  const [isGenerateSegmentsOpen, setIsGenerateSegmentsOpen] = useState(false);
  const [isGeneratingSegments, setIsGeneratingSegments] = useState(false);
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [isBrandKitOpen, setIsBrandKitOpen] = useState(false);
  const [segmentName, setSegmentName] = useState('');
  const [selectedSegmentName, setSelectedSegmentName] = useState('');
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  /** Type of the selected item: 'segment' | 'topic' | 'clip'. Used for modal title/label. */
  const [selectedFragmentType, setSelectedFragmentType] = useState<'segment' | 'topic' | 'clip'>(
    'segment',
  );
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<'16:9' | '4:3' | '1:1'>('16:9');
  const [selectedBrandOptions, setSelectedBrandOptions] = useState<BrandOptionKey[]>(['outro']);
  const [logoPosition, setLogoPosition] = useState<string>('top-right');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  /** Timestamp of overlay history entry whose transcript panel is expanded (null = all collapsed). */
  const [expandedTranscriptTimestamp, setExpandedTranscriptTimestamp] = useState<number | null>(
    null,
  );
  /** Whether the segment transcript is shown in a popout window. */
  const [isSegmentTranscriptPoppedOut, setIsSegmentTranscriptPoppedOut] = useState(false);
  /** Whether portrait (9:16) video is available (file exists and can be fetched). */
  const [isPortraitAvailable, setIsPortraitAvailable] = useState(false);
  /** Whether square (1:1) video is available (file exists and can be fetched). */
  const [isSquareAvailable, setIsSquareAvailable] = useState(false);
  const progressPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const portraitPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const squarePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { data: brandKitAssets, isLoading: isBrandKitLoading } = useGetScopedBrandKit(
    { projectId },
    isBrandKitOpen && Boolean(projectId),
  );
  const { mutateAsync: uploadScopedBrandKitAsset } = useUploadScopedBrandKitAsset();
  const [localBrandKitPreviews, setLocalBrandKitPreviews] = useState<{
    intro?: { path: string | null; s3Path: string | null };
    logo?: { path: string | null; s3Path: string | null };
    outro?: { path: string | null; s3Path: string | null };
  }>({});
  /** Options for the overlay API when export completes (aspect ratio + brand assets). */
  const pendingOverlayOptionsRef = useRef<{
    aspectRatio: '16:9' | '4:3' | '1:1';
    brandOptions: BrandOptionKey[];
    introPath: string | null;
    outroPath: string | null;
    logoPath: string | null;
    /** Segment id from project.edits (backend segment id) – use this for overlay PATCH, not timeline fragment id. */
    effectiveSegmentId: string | null;
  } | null>(null);

  const handleCustomizeClick = useCallback((): void => {
    setIsCustomizeMode(true);
    setShowRunsheetSelector(false);
  }, []);

  const handleCustomizeSave = useCallback((): void => {
    setIsCustomizeMode(false);
  }, []);

  const handleToggleRunsheetSelector = useCallback((): void => {
    setShowRunsheetSelector((prev) => !prev);
  }, []);

  const handleRunsheetChanged = useCallback((): void => {
    void refetch();
    setShowRunsheetSelector(false);
    if (project?.streamId) {
      handleFragmentsRebuild();
      EXTERNAL_CALLBACK_onNewFragments(0, project.streamId);
    }
  }, [refetch, project?.streamId, handleFragmentsRebuild, EXTERNAL_CALLBACK_onNewFragments]);

  /** Refresh project data and brand kit assets so the branding sidebar stays in sync. */
  const refetchBrandingSidebar = useCallback((): void => {
    void refetch();
    void queryClient.invalidateQueries(['scoped-brand-kit', projectId]);
  }, [refetch, queryClient, projectId]);

  /** Refresh sidebar data every time it is opened so previews are up-to-date without a browser reload. */
  useEffect(() => {
    if (isBrandKitOpen) {
      void refetch();
      void queryClient.invalidateQueries(['scoped-brand-kit', projectId]);
    }
  }, [isBrandKitOpen, refetch, queryClient, projectId]);

  const handleQuoteDetection = useCallback(async (): Promise<void> => {
    if (!project?.streamId || isQuoteDetecting) return;
    setIsQuoteDetecting(true);
    try {
      const result = await runQuoteDetection(project.streamId);
      console.log('[QuoteDetection] Result:', result);
      void refetch();
    } catch (err) {
      console.error('[QuoteDetection] Error:', err);
    } finally {
      setIsQuoteDetecting(false);
    }
  }, [project?.streamId, isQuoteDetecting, refetch]);

  const handleGenerateSegments = useCallback(
    async (numberOfSegments: number): Promise<void> => {
      if (!project?.streamId || isGeneratingSegments) return;
      setIsGeneratingSegments(true);
      try {
        const created = await generateSegmentFragments(project.streamId, numberOfSegments);
        enqueueSnackbar(`Created ${created.length} segment fragment(s)`, { variant: 'success' });
        setIsGenerateSegmentsOpen(false);
        void refetch();
      } catch (err) {
        console.error('[GenerateSegments] Error:', err);
        enqueueSnackbar(err instanceof Error ? err.message : 'Failed to generate segments', {
          variant: 'error',
        });
      } finally {
        setIsGeneratingSegments(false);
      }
    },
    [project?.streamId, isGeneratingSegments, enqueueSnackbar, refetch],
  );

  /** Stable callback for AVDisplay segment selection (avoids infinite loop from effect deps). */
  const handleSelectedSegmentChange = useCallback(
    (segment: { id: string; name: string; type?: 'segment' | 'topic' | 'clip' } | null) => {
      setSelectedSegmentId(segment?.id ?? null);
      setSelectedSegmentName(segment?.name ?? '');
      setSelectedFragmentType(segment?.type ?? 'segment');
    },
    [],
  );

  // Check if user has role "user". `isEditorRole` is only true once the role
  // has loaded AND it's NOT 'user', so gating editor-only UI on it hides
  // those controls during the login/role-loading window instead of letting
  // them flash in and then disappear.
  const isUserRole = useMemo(() => userState?.role === 'user', [userState?.role]);
  const isEditorRole = useMemo(
    () => !!userState?.role && userState.role !== 'user',
    [userState?.role],
  );
  // Bumped whenever the speaker's wavesurfer signals it finished decoding
  // a swapped-in audio source. Used as a useEffect dep so AVDisplay's
  // region pass re-runs against the now-correct waveform duration —
  // otherwise the segment region is sized for the dummy 5-min duration
  // and clicks miss.
  const [speakerWaveReadyTick, setSpeakerWaveReadyTick] = useState(0);
  // Authoritative duration reported by wavesurfer once the swapped-in
  // segment audio is decoded. Used alongside the <audio> probe so the
  // segment row's end time can land on the correct value from whichever
  // signal arrives first — and not stay stuck on the PG fragment's
  // (much-longer) full-stream window until the user clicks.
  const [waveReportedDurationSec, setWaveReportedDurationSec] = useState<number | null>(null);
  const { onAvComponentReady, handleStop, peakDataProcessor } = useAudioLoader(
    project?.streamId as string,
    {
      // Speakers don't subscribe to the worker peak stream; instead wavesurfer
      // decodes the segment audio directly so the waveform reflects only the
      // speaker's segment.
      isSpeaker: isUserRole,
      audioUrlOverride: isUserRole ? project?.audio?.fullPath : undefined,
      onSpeakerWaveReady: (durationSec: number) => {
        setSpeakerWaveReadyTick((n) => n + 1);
        if (durationSec > 0) setWaveReportedDurationSec(durationSec);
      },
    },
  );

  // Speakers: probe the rendered segment MP4 for its actual length. The DDB
  // cut range and PG fragment window can both drift from the rendered
  // file's duration (encoding/keyframe alignment trims a few seconds), so
  // anchoring `contentDurationSec`, the timeline bar label, the segment
  // row's end time, and the aspect-ratio rule on the *real* duration
  // keeps every displayed time in sync with playback.
  const speakerSegmentUrl = isUserRole ? project?.original?.fullPath ?? null : null;
  const [probedSegmentDurationSec, setProbedSegmentDurationSec] = useState<number | null>(null);
  useEffect(() => {
    if (!speakerSegmentUrl) {
      setProbedSegmentDurationSec(null);
      return;
    }
    let cancelled = false;
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.crossOrigin = 'anonymous';
    audio.src = speakerSegmentUrl;
    const onLoaded = (): void => {
      if (cancelled) return;
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setProbedSegmentDurationSec(audio.duration);
      }
    };
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('durationchange', onLoaded);
    return (): void => {
      cancelled = true;
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('durationchange', onLoaded);
      audio.removeAttribute('src');
      audio.load();
    };
  }, [speakerSegmentUrl]);

  const formattedProjectDate = useMemo(() => {
    if (!project?.created) return '';
    return new Date(project.created).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, [project?.created]);

  /** Resolve a segment path (s3 or signed URL) to an HTTP URL – same as Projects page. */
  const resolveSegmentPath = useCallback((path: string | undefined): string | null => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    try {
      if (path.startsWith('s3://')) return convertS3PathToHttpUrl(path);
    } catch {
      return null;
    }
    return null;
  }, []);

  /** Selected segment edit – match by id, fragmentId (timeline fragment id), or name so preview works when backend segment id differs from timeline id. */
  const selectedEdit = useMemo(() => {
    if (!project?.edits.length) return null;
    const byId = project.edits.find((e) => String(e.id) === String(selectedSegmentId));
    if (byId) return byId;
    const byFragmentId = project.edits.find(
      (e) =>
        (e as { fragmentId?: string }).fragmentId != null &&
        String((e as { fragmentId?: string }).fragmentId) === String(selectedSegmentId),
    );
    if (byFragmentId) return byFragmentId;
    const nameToMatch = (selectedSegmentName || segmentName || '').trim().toLowerCase();
    if (nameToMatch) {
      const byName = project.edits.find((e) => (e.name || '').trim().toLowerCase() === nameToMatch);
      if (byName) return byName;
    }
    return null;
  }, [project?.edits, selectedSegmentId, selectedSegmentName, segmentName]);

  /** Segment video URLs for all 3 aspect ratios (original, portrait, square) – same endpoints as Projects page. */
  const segmentPreviewUrlsByFormat = useMemo(() => {
    if (!selectedEdit) return { original: null, portrait: null, square: null };
    const edit = selectedEdit as { pathKey?: string; pathPortrait?: string; pathSquare?: string };
    return {
      original: resolveSegmentPath(edit.pathKey),
      portrait: resolveSegmentPath(edit.pathPortrait),
      square: resolveSegmentPath(edit.pathSquare),
    };
  }, [selectedEdit, resolveSegmentPath]);

  /**
   * Poll portrait/square URLs to check if reframed videos are available.
   * Resets availability when segment changes, then polls until files exist.
   */
  useEffect(() => {
    // Reset availability when segment changes
    setIsPortraitAvailable(false);
    setIsSquareAvailable(false);

    // Clear existing polling
    if (portraitPollingRef.current) {
      clearInterval(portraitPollingRef.current);
      portraitPollingRef.current = null;
    }
    if (squarePollingRef.current) {
      clearInterval(squarePollingRef.current);
      squarePollingRef.current = null;
    }

    const checkAvailability = async (
      url: string,
      setAvailable: (v: boolean) => void,
      pollingRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>,
    ): Promise<void> => {
      try {
        const controller = new AbortController();
        const response = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
          signal: controller.signal,
        });
        controller.abort();
        if (response.ok || response.status === 206) {
          setAvailable(true);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch {
        // File not ready yet, continue polling
      }
    };

    // Start polling for portrait if URL exists
    if (segmentPreviewUrlsByFormat.portrait) {
      const url = segmentPreviewUrlsByFormat.portrait;
      void checkAvailability(url, setIsPortraitAvailable, portraitPollingRef);
      portraitPollingRef.current = setInterval(() => {
        void checkAvailability(url, setIsPortraitAvailable, portraitPollingRef);
      }, 5000);
    }

    // Start polling for square if URL exists
    if (segmentPreviewUrlsByFormat.square) {
      const url = segmentPreviewUrlsByFormat.square;
      void checkAvailability(url, setIsSquareAvailable, squarePollingRef);
      squarePollingRef.current = setInterval(() => {
        void checkAvailability(url, setIsSquareAvailable, squarePollingRef);
      }, 5000);
    }

    return () => {
      if (portraitPollingRef.current) {
        clearInterval(portraitPollingRef.current);
        portraitPollingRef.current = null;
      }
      if (squarePollingRef.current) {
        clearInterval(squarePollingRef.current);
        squarePollingRef.current = null;
      }
    };
  }, [segmentPreviewUrlsByFormat.portrait, segmentPreviewUrlsByFormat.square]);

  /** Segment video URL for preview (selected aspect ratio). Same source as Projects page. */
  const segmentPreviewUrl = useMemo(() => {
    const url =
      selectedAspectRatio === '4:3'
        ? segmentPreviewUrlsByFormat.portrait
        : selectedAspectRatio === '1:1'
        ? segmentPreviewUrlsByFormat.square
        : segmentPreviewUrlsByFormat.original;
    return url ?? null;
  }, [segmentPreviewUrlsByFormat, selectedAspectRatio]);

  /** Branded overlay URLs by format from overlayHistory (Processed entries only) – used so preview shows actual branded video when available. */
  const brandedPreviewUrlsByFormat = useMemo(() => {
    const edit = selectedEdit as {
      overlayHistory?: { overlayPath: string; format: string; status?: string }[];
    } | null;
    const history = edit?.overlayHistory ?? [];
    const byFormat: { original: string | null; portrait: string | null; square: string | null } = {
      original: null,
      portrait: null,
      square: null,
    };
    for (const format of ['original', 'portrait', 'square'] as const) {
      const completed = history.filter((h) => h.status === 'Processed' && h.format === format);
      const latest = completed.length > 0 ? completed[completed.length - 1] : null;
      if (latest?.overlayPath) {
        const url = latest.overlayPath.startsWith('http')
          ? latest.overlayPath
          : (() => {
              try {
                return convertS3PathToHttpUrl(latest.overlayPath);
              } catch {
                return null;
              }
            })();
        if (url) byFormat[format] = url;
      }
    }
    return byFormat;
  }, [selectedEdit]);

  /** Effective preview URL: branded overlay for selected aspect ratio when available, else base segment.
   * For original format clips without branded overlay, use HLS stream instead of non-existent MP4.
   */
  const effectivePreviewUrl = useMemo(() => {
    const format =
      selectedAspectRatio === '4:3'
        ? 'portrait'
        : selectedAspectRatio === '1:1'
        ? 'square'
        : 'original';
    const branded = brandedPreviewUrlsByFormat[format];
    if (branded) return branded;
    // For original format, use HLS stream (clips don't have pre-rendered original MP4s)
    if (format === 'original' && project?.streamPath) {
      return project.streamPath;
    }
    return segmentPreviewUrl;
  }, [brandedPreviewUrlsByFormat, selectedAspectRatio, segmentPreviewUrl, project?.streamPath]);

  /** True when preview should use HLS player (original format without branded overlay). */
  const isHlsPreview = useMemo(() => {
    const format =
      selectedAspectRatio === '4:3'
        ? 'portrait'
        : selectedAspectRatio === '1:1'
        ? 'square'
        : 'original';
    const branded = brandedPreviewUrlsByFormat[format];
    return format === 'original' && !branded && Boolean(project?.streamPath);
  }, [selectedAspectRatio, brandedPreviewUrlsByFormat, project?.streamPath]);

  /** Start time in seconds for HLS preview (clip start time). */
  const hlsPreviewStartTimeSec = useMemo(() => {
    if (!selectedEdit) return 0;
    const edit = selectedEdit as { timing?: { start: number; end: number } };
    // timing.start is in seconds
    return edit.timing?.start ?? 0;
  }, [selectedEdit]);

  /** End time in seconds for HLS preview (clip end time). */
  const hlsPreviewEndTimeSec = useMemo(() => {
    if (!selectedEdit) return undefined;
    const edit = selectedEdit as { timing?: { start: number; end: number } };
    // timing.end is in seconds
    return edit.timing?.end;
  }, [selectedEdit]);

  /** True when preview is showing a completed branded overlay (do not overlay logo/intro/outro client-side). */
  const isBrandedPreview = useMemo(() => {
    const format =
      selectedAspectRatio === '4:3'
        ? 'portrait'
        : selectedAspectRatio === '1:1'
        ? 'square'
        : 'original';
    return Boolean(brandedPreviewUrlsByFormat[format]);
  }, [brandedPreviewUrlsByFormat, selectedAspectRatio]);

  const effectiveBrandKitAssets = useMemo(() => {
    const getProjectOverlayAsset = (key: 'intro' | 'logo' | 'outro'): BrandKitPreviewAsset => {
      const currentEditOverlayPath = (
        selectedEdit as {
          overlaySettings?: { intro?: string; logo?: string; outro?: string };
        } | null
      )?.overlaySettings?.[key];
      const fallbackEditOverlayPath = (
        project?.edits[0] as
          | {
              overlaySettings?: { intro?: string; logo?: string; outro?: string };
            }
          | undefined
      )?.overlaySettings?.[key];
      const rawPath = currentEditOverlayPath ?? fallbackEditOverlayPath ?? null;
      if (!rawPath) return { path: null, s3Path: null };
      return {
        path: rawPath,
        s3Path: rawPath.startsWith('s3://') ? rawPath : null,
      };
    };

    return {
      intro:
        localBrandKitPreviews.intro ?? brandKitAssets?.intro ?? getProjectOverlayAsset('intro'),
      logo: localBrandKitPreviews.logo ?? brandKitAssets?.logo ?? getProjectOverlayAsset('logo'),
      outro:
        localBrandKitPreviews.outro ?? brandKitAssets?.outro ?? getProjectOverlayAsset('outro'),
    };
  }, [brandKitAssets, localBrandKitPreviews, project?.edits, selectedEdit]);

  /** Asset-only path (intro/outro/logo) for fallback when segment video isn't available yet. */
  const previewBrandKitPath = useMemo(() => {
    if (!brandKitAssets) return null;
    if (selectedBrandOptions.includes('intro')) return effectiveBrandKitAssets.intro.path;
    if (selectedBrandOptions.includes('outro')) return effectiveBrandKitAssets.outro.path;
    if (selectedBrandOptions.includes('logo')) return effectiveBrandKitAssets.logo.path;
    return null;
  }, [brandKitAssets, effectiveBrandKitAssets, selectedBrandOptions]);

  const handleBrandKitUpload = useCallback(
    async (type: BrandKitAssets, file: File): Promise<void> => {
      const localPreview = URL.createObjectURL(file);
      setLocalBrandKitPreviews((prev) => ({
        ...prev,
        [type]: { path: localPreview, s3Path: prev[type]?.s3Path ?? null },
      }));
      try {
        const result = await uploadScopedBrandKitAsset({ type, file, projectId });
        setLocalBrandKitPreviews((prev) => ({
          ...prev,
          [type]: { path: localPreview, s3Path: result.s3Path },
        }));
      } catch (error) {
        console.error('Failed to upload scoped brand kit asset', error);
      }
    },
    [projectId, uploadScopedBrandKitAsset],
  );
  console.log('🚀 ~ Workbench ~ previewBrandKitPath:', previewBrandKitPath);

  /**
   * Segment is ready for export based on selected aspect ratio:
   * - Original 16:9: use HLS stream (no segment MP4 needed)
   * - Portrait 4:3: need pre-rendered pathPortrait MP4
   * - Square 1:1: need pre-rendered pathSquare MP4
   * Also ready if renderStatus === 'SCHEDULED' or status === 'Processed'.
   */
  const isSelectedSegmentProcessed = useMemo(() => {
    if (!selectedEdit) return false;
    const edit = selectedEdit as {
      renderStatus?: string;
      status?: string;
      pathKey?: string;
      path?: string;
      pathPortrait?: string;
      pathSquare?: string;
      timing?: { start: number; end: number };
    };

    // Common conditions that work for any format
    if (edit.renderStatus === 'SCHEDULED' || edit.status === 'Processed') {
      return true;
    }

    // Format-specific checks
    if (selectedAspectRatio === '16:9') {
      // For original 16:9: use HLS stream if available and segment has timing
      if (project?.streamPath && edit.timing) {
        return true;
      }
      // Also allow if segment has its own MP4
      return Boolean(edit.pathKey) || Boolean(edit.path);
    } else if (selectedAspectRatio === '4:3') {
      // For portrait: need the reframed MP4 to be available (not just path exists)
      return isPortraitAvailable;
    } else if (selectedAspectRatio === '1:1') {
      // For square: need the reframed MP4 to be available (not just path exists)
      return isSquareAvailable;
    }

    return false;
  }, [
    selectedEdit,
    selectedAspectRatio,
    project?.streamPath,
    isPortraitAvailable,
    isSquareAvailable,
  ]);

  /** Segment id from project.edits to use for overlay API – must match backend segment id (same as Projects page). */
  const effectiveSegmentIdForOverlay = useMemo(
    (): string | null => (selectedEdit ? String(selectedEdit.id) : null),
    [selectedEdit],
  );

  const handleSendSpeakerInvites = useCallback(
    async (overlayTimestamp: number): Promise<void> => {
      if (!projectId || !effectiveSegmentIdForOverlay) {
        enqueueSnackbar('Select a segment with an exported version first', {
          variant: 'warning',
          autoHideDuration: 500,
        });
        return;
      }
      const segmentId = effectiveSegmentIdForOverlay;
      setPendingInviteTimestamp(overlayTimestamp);
      try {
        const result = await inviteSegmentSpeakers.mutateAsync({ projectId, segmentId });
        if (result.emailsAttempted === 0) {
          enqueueSnackbar('No speaker emails are attached to this segment', {
            variant: 'info',
            autoHideDuration: 500,
          });
          return;
        }
        const c = result.counts ?? {
          invited: 0,
          linked: 0,
          different_org: 0,
          invalid_email: 0,
          auth0_not_configured: 0,
          failed: 0,
        };
        const parts: string[] = [];
        if (c.invited) parts.push(`${c.invited} invited`);
        if (c.linked) parts.push(`${c.linked} already had accounts`);
        if (c.different_org) parts.push(`${c.different_org} in another org (skipped)`);
        if (c.invalid_email) parts.push(`${c.invalid_email} invalid`);
        if (c.failed) parts.push(`${c.failed} failed`);
        const variant: 'success' | 'warning' =
          c.failed > 0 || c.different_org > 0 || c.invalid_email > 0 ? 'warning' : 'success';
        enqueueSnackbar(parts.join(', ') || 'Invites processed', {
          variant,
          autoHideDuration: 500,
        });
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Failed to send invites', {
          variant: 'error',
          autoHideDuration: 500,
        });
      } finally {
        setPendingInviteTimestamp(null);
      }
    },
    [projectId, effectiveSegmentIdForOverlay, inviteSegmentSpeakers, enqueueSnackbar],
  );
  console.log('🚀 ~ Workbench ~ effectiveSegmentIdForOverlay:', effectiveSegmentIdForOverlay);

  /** Content duration in seconds for timeline bar (from selected segment edit). */
  const contentDurationSec = useMemo((): number => {
    // Speakers viewing their own segment: prefer the probed media duration
    // (real rendered MP4 length) over the DDB cut range or PG fragment
    // window, so the timeline label, aspect-ratio rule, and per-segment
    // end time all match playback. Only applies when the speaker has
    // their *segment* selected — clip rows fall through to the standard
    // selectedEdit.timing math below so a short clip isn't blocked by
    // the segment-length cap.
    if (isUserRole && selectedFragmentType === 'segment') {
      // Use the <audio>-probed duration as the source of truth for
      // displayed times — it matches the video player's duration. The
      // wavesurfer-reported duration can lag at 300 (the seeded dummy
      // value) when the decode event never overrides options.duration,
      // so we explicitly skip 300 from that source.
      const fromWavesurfer =
        waveReportedDurationSec && waveReportedDurationSec > 0 && waveReportedDurationSec !== 300
          ? waveReportedDurationSec
          : null;
      const speakerDurationSec = probedSegmentDurationSec ?? fromWavesurfer;
      if (speakerDurationSec && speakerDurationSec > 0) {
        return Math.round(speakerDurationSec);
      }
    }
    if (!selectedEdit) return 0;
    const edit = selectedEdit as {
      timing?: { start: number; end: number };
      overlay?: { startTimestamp?: number; endTimestamp?: number };
    };
    if (
      edit.timing &&
      typeof edit.timing.end === 'number' &&
      typeof edit.timing.start === 'number'
    ) {
      return Math.max(0, Math.round(edit.timing.end - edit.timing.start));
    }
    if (
      edit.overlay &&
      typeof (edit.overlay as { endTimestamp?: number }).endTimestamp === 'number' &&
      typeof (edit.overlay as { startTimestamp?: number }).startTimestamp === 'number'
    ) {
      const o = edit.overlay as { startTimestamp: number; endTimestamp: number };
      return Math.max(0, Math.round(o.endTimestamp - o.startTimestamp));
    }
    return 0;
  }, [
    selectedEdit,
    isUserRole,
    selectedFragmentType,
    probedSegmentDurationSec,
    waveReportedDurationSec,
  ]);

  /** Transcript items filtered to the selected segment/clip/topic time range. */
  const segmentTranscriptItems = useMemo(() => {
    if (!selectedEdit || !transcriptItems.length) return [];
    const edit = selectedEdit as {
      timing?: { start: number; end: number };
      overlay?: { startTimestamp?: number; endTimestamp?: number };
    };
    let start = 0;
    let end = 0;
    if (
      edit.timing &&
      typeof edit.timing.start === 'number' &&
      typeof edit.timing.end === 'number'
    ) {
      start = edit.timing.start;
      end = edit.timing.end;
    } else if (
      edit.overlay &&
      typeof (edit.overlay as { startTimestamp?: number }).startTimestamp === 'number' &&
      typeof (edit.overlay as { endTimestamp?: number }).endTimestamp === 'number'
    ) {
      const o = edit.overlay as { startTimestamp: number; endTimestamp: number };
      start = o.startTimestamp;
      end = o.endTimestamp;
    }
    if (start === 0 && end === 0) return transcriptItems;
    return transcriptItems.filter((item) => item.start >= start && item.end <= end);
  }, [selectedEdit, transcriptItems]);

  /**
   * Aspect ratio availability:
   * - 16:9 (original): always available when segment has timing (uses HLS, no processing needed)
   * - 4:3/1:1 (portrait/square): only for segments under 5 minutes (requires reframing)
   */
  const FIVE_MINUTES_SEC = 5 * 60;
  const is16x9Enabled = contentDurationSec > 0;
  const isReframingEnabled = contentDurationSec > 0 && contentDurationSec < FIVE_MINUTES_SEC;

  // Existing exported overlays for this project (used to surface downloads in the popup)
  const projectOverlays = useMemo(
    () =>
      (project?.edits ?? [])
        .flatMap((edit) =>
          (edit.overlayHistory ?? []).map((overlay) => ({
            editName: edit.name,
            overlay,
          })),
        )
        .sort((a, b) => b.overlay.timestamp - a.overlay.timestamp),
    [project?.edits],
  );

  const filteredProjectOverlays = useMemo(() => {
    const name = segmentName.trim().toLowerCase();
    if (!name) return projectOverlays;
    return projectOverlays.filter(({ editName }) => editName.toLowerCase().includes(name));
  }, [projectOverlays, segmentName]);
  console.log('🚀 ~ Workbench ~ filteredProjectOverlays:', filteredProjectOverlays);

  /** Get a direct download URL for a processed segment (pathKey or fullPath). */
  const getSegmentDownloadUrl = (edit: { pathKey?: string; fullPath?: string }): string | null => {
    const path = edit.pathKey ?? edit.fullPath;
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    try {
      if (path.startsWith('s3://')) return convertS3PathToHttpUrl(path);
    } catch {
      return null;
    }
    return null;
  };

  interface ProjectSegmentItem {
    id: string | number;
    name: string;
    status: 'Processed' | 'Processing';
    overlayHistory: { overlayPath: string; format: string; timestamp: number; status?: string }[];
    segmentDownloadUrl: string | null;
  }

  const projectSegments = useMemo(
    (): ProjectSegmentItem[] =>
      (project?.edits ?? []).map((edit) => {
        const editWithPaths = edit as {
          pathKey?: string;
          path?: string;
          pathPortrait?: string;
          pathSquare?: string;
          renderStatus?: string;
          status?: string;
          overlayHistory?: {
            overlayPath: string;
            format: string;
            timestamp: number;
            status?: string;
          }[];
        };
        const overlayHistory = editWithPaths.overlayHistory ?? [];
        const segmentDownloadUrl = getSegmentDownloadUrl(
          edit as { pathKey?: string; fullPath?: string },
        );
        const hasPath =
          Boolean(editWithPaths.pathKey) ||
          Boolean(editWithPaths.path) ||
          Boolean(editWithPaths.pathPortrait) ||
          Boolean(editWithPaths.pathSquare);
        const status: 'Processed' | 'Processing' =
          editWithPaths.renderStatus === 'SCHEDULED' ||
          editWithPaths.status === 'Processed' ||
          hasPath
            ? 'Processed'
            : 'Processing';
        return {
          id: edit.id,
          name: edit.name,
          status,
          overlayHistory,
          segmentDownloadUrl,
        };
      }),
    [project?.edits],
  );

  const filteredProjectSegments = useMemo(() => {
    const name = segmentName.trim().toLowerCase();
    if (!name) return projectSegments;
    return projectSegments.filter((s) => s.name.toLowerCase().includes(name));
  }, [projectSegments, segmentName]);
  console.log('🚀 ~ Workbench ~ filteredProjectSegments:', filteredProjectSegments);

  /** Effective segment name for export: input value or selected segment name so Export is enabled when a processed segment is selected. */
  const effectiveSegmentName = (segmentName || selectedSegmentName || '').trim();
  console.log('🚀 ~ Workbench ~ effectiveSegmentName:', effectiveSegmentName);

  // When Brand Kit is open and selection changes, keep segment name in sync so Export can be enabled
  useEffect(() => {
    if (isBrandKitOpen && selectedSegmentName) setSegmentName(selectedSegmentName);
  }, [isBrandKitOpen, selectedSegmentName]);

  // For segments >= 5 minutes, force 16:9 (portrait/square reframing not available)
  useEffect(() => {
    if (!isReframingEnabled && selectedAspectRatio !== '16:9') {
      setSelectedAspectRatio('16:9');
    }
  }, [isReframingEnabled, selectedAspectRatio]);

  // Listen for export status events from AVDisplay so we can show feedback in the popup
  useEffect(() => {
    const handleExportError = (event: Event): void => {
      const { reason } = (event as CustomEvent<{ reason?: string }>).detail || {};
      if (reason === 'no-segment-selected') {
        setExportError('Select a segment in the timeline to export.');
      } else {
        setExportError('Unable to start export. Please try again.');
      }
      setIsExporting(false);
    };

    const handleExportStarted = (): void => {
      setIsExporting(false);
      setExportError(null);
    };

    const handleExportCompleted = async (event: Event): Promise<void> => {
      const detail = (
        event as CustomEvent<{
          segmentId: string;
          segmentName: string;
          startTimestamp: number;
          endTimestamp: number;
        }>
      ).detail;
      const opts = pendingOverlayOptionsRef.current;
      pendingOverlayOptionsRef.current = null;

      void refetch();
      void queryClient.invalidateQueries(['scoped-brand-kit', projectId]);

      if (!projectId) return;

      // Use segment id from project.edits (backend segment id) so overlay matches Projects page; fallback to timeline segment id
      const segId = opts?.effectiveSegmentId || detail.segmentId;
      if (!segId) return;
      const overlayPayload = {
        name: detail.segmentName,
        overlay: {
          startTimestamp: String(detail.startTimestamp ?? 0),
          endTimestamp: String(detail.endTimestamp ?? 0),
        },
        overlaySettings: {
          color: 'black',
          fontSize: '24',
          font: 'Roboto',
          justified: 'top center',
          enableLogo: Boolean(opts?.brandOptions.includes('logo')),
          enableIntro: Boolean(opts?.brandOptions.includes('intro')),
          enableOutro: Boolean(opts?.brandOptions.includes('outro')),
          ...(opts?.brandOptions.includes('logo') && opts.logoPath && { logo: opts.logoPath }),
          ...(opts?.brandOptions.includes('intro') && opts.introPath && { intro: opts.introPath }),
          ...(opts?.brandOptions.includes('outro') && opts.outroPath && { outro: opts.outroPath }),
        },
      };

      const formats: ('original' | 'portrait' | 'square')[] = ['original', 'portrait', 'square'];
      try {
        for (const selectedFormat of formats) {
          await ApiV2.patch(`/api/projects/${projectId}/segments/${segId}/overlay`, {
            ...overlayPayload,
            selectedFormat,
          });
        }
      } catch (err) {
        console.error('Overlay request failed:', err);
      }

      setTimeout(() => {
        void refetch();
        void queryClient.invalidateQueries(['scoped-brand-kit', projectId]);
      }, 1500);
    };

    window.addEventListener('eos-export-clip-error', handleExportError as EventListener);
    window.addEventListener('eos-export-clip-started', handleExportStarted as EventListener);
    window.addEventListener('eos-export-clip-completed', handleExportCompleted as EventListener);

    return () => {
      window.removeEventListener('eos-export-clip-error', handleExportError as EventListener);
      window.removeEventListener('eos-export-clip-started', handleExportStarted as EventListener);
      window.removeEventListener(
        'eos-export-clip-completed',
        handleExportCompleted as EventListener,
      );
    };
  }, [refetch, projectId, queryClient]);

  /** Map Brandkit aspect ratio to API selectedFormat (same as Projects page). */
  const aspectRatioToFormat = useCallback(
    (ratio: '16:9' | '4:3' | '1:1'): 'original' | 'portrait' | 'square' => {
      if (ratio === '4:3') return 'portrait';
      if (ratio === '1:1') return 'square';
      return 'original';
    },
    [],
  );

  /** Extract S3 bucket from project stream path for progress URL (same as ProjectVideo). */
  const getBucketFromStreamPath = useCallback((streamPath: string | undefined): string => {
    if (!streamPath) return 'your-default-bucket';
    try {
      const streamUrl = new URL(streamPath);
      const hostParts = streamUrl.hostname.split('.');
      if (hostParts.length >= 3 && (hostParts[1] === 's3' || hostParts[1].startsWith('s3-'))) {
        return hostParts[0];
      }
    } catch {
      // ignore
    }
    return 'your-default-bucket';
  }, []);

  /** Deterministic filename for overlay download (same as Projects page). */
  const getOverlayFileName = useCallback(
    (overlay: { format?: string; timestamp: number }) => {
      const safeBaseName =
        (selectedEdit as { name?: string }).name ?? project?.name ?? `overlay-${overlay.timestamp}`;
      const formatSuffix = overlay.format ? `-${overlay.format}` : '';
      return `${safeBaseName}${formatSuffix}-${overlay.timestamp}.mp4`;
    },
    [selectedEdit, project?.name],
  );

  /** Poll overlay progress and on completion refetch project and set download URL (same flow as Projects Save changes). */
  const startOverlayProgressPolling = useCallback(
    (segId: string, format: 'original' | 'portrait' | 'square') => {
      if (progressPollingRef.current) {
        clearInterval(progressPollingRef.current);
        progressPollingRef.current = null;
      }
      const bucket = getBucketFromStreamPath(project?.streamPath);
      const progressUrl = `https://${bucket}.s3.amazonaws.com/progress/${
        projectId ?? ''
      }/${segId}/progress.html`;
      const TEN_MIN_MS = 600000;
      const pollInterval = setInterval(async () => {
        try {
          const response = await fetch(progressUrl);
          if (response.ok) {
            const htmlContent = await response.text();
            if (htmlContent.includes('class="completed"')) {
              if (progressPollingRef.current) {
                clearInterval(progressPollingRef.current);
                progressPollingRef.current = null;
              }
              void refetch();
              void queryClient.invalidateQueries(['scoped-brand-kit', projectId]);
            }
          }
        } catch (error) {
          console.error('Overlay progress polling error:', error);
        }
      }, 3000);
      progressPollingRef.current = pollInterval;
      setTimeout(() => {
        if (progressPollingRef.current === pollInterval) {
          clearInterval(pollInterval);
          progressPollingRef.current = null;
          setExportError('Processing timeout. Please refresh and try again.');
        }
      }, TEN_MIN_MS);
    },
    [projectId, project?.streamPath, getBucketFromStreamPath, refetch, queryClient],
  );

  /** Save changes flow: same as Projects page – PATCH overlay with branding, then poll until done and show download. */
  const handleExportClipDirect = useCallback(async () => {
    if (!projectId || !selectedEdit || !effectiveSegmentName.trim()) return;
    const segId = String(selectedEdit.id);
    const format = aspectRatioToFormat(selectedAspectRatio);
    // Prefer the waveform-updated fragment timing (PostgreSQL) over the stale
    // DynamoDB-sourced selectedEdit.timing so re-exports pick up duration edits.
    const fragmentId = (selectedEdit as { fragmentId?: string }).fragmentId;
    const currentFragment = fragmentId
      ? fragments.find((f) => String(f.id) === String(fragmentId))
      : undefined;
    const timing = currentFragment
      ? {
          start: (currentFragment.timeStartAdjusted ?? 0) / 100,
          end: (currentFragment.timeEndAdjusted ?? 0) / 100,
        }
      : (selectedEdit as { timing?: { start: number; end: number } }).timing ?? {
          start: 0,
          end: 0,
        };
    const overlayPayload = {
      name: effectiveSegmentName.trim().replace(/\s+/g, '-'),
      selectedFormat: format,
      overlay: {
        startTimestamp: String(timing.start),
        endTimestamp: String(timing.end),
      },
      overlaySettings: {
        color: 'black',
        fontSize: '24',
        font: 'Roboto',
        justified: 'top center',
        enableLogo: selectedBrandOptions.includes('logo'),
        enableIntro: selectedBrandOptions.includes('intro'),
        enableOutro: selectedBrandOptions.includes('outro'),
        ...(selectedBrandOptions.includes('logo') && {
          logoPosition: logoPosition,
        }),
        ...(selectedBrandOptions.includes('logo') &&
          effectiveBrandKitAssets.logo.s3Path && { logo: effectiveBrandKitAssets.logo.s3Path }),
        ...(selectedBrandOptions.includes('intro') &&
          effectiveBrandKitAssets.intro.s3Path && {
            intro: effectiveBrandKitAssets.intro.s3Path,
          }),
        ...(selectedBrandOptions.includes('outro') &&
          effectiveBrandKitAssets.outro.s3Path && {
            outro: effectiveBrandKitAssets.outro.s3Path,
          }),
      },
    };
    console.log('🚀 overlayPayload:', JSON.stringify(overlayPayload, null, 2));
    console.log('🚀 logoPosition state:', logoPosition);
    setExportError(null);
    setIsExporting(true);
    try {
      await ApiV2.patch(`/api/projects/${projectId}/segments/${segId}/overlay`, overlayPayload);
      // Refetch immediately so the new history entry appears right away
      void refetch();
      startOverlayProgressPolling(segId, format);
    } catch (err) {
      console.error('Overlay request failed:', err);
      setExportError('Failed to start export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [
    projectId,
    selectedEdit,
    effectiveSegmentName,
    selectedAspectRatio,
    selectedBrandOptions,
    logoPosition,
    aspectRatioToFormat,
    startOverlayProgressPolling,
    effectiveBrandKitAssets,
    fragments,
  ]);

  const toggleBrandOption = useCallback((option: BrandOptionKey): void => {
    setSelectedBrandOptions((prev) =>
      prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option],
    );
  }, []);

  // const toggleAllBrandOptions = useCallback((): void => {
  //   const allOptions: BrandOptionKey[] = ['intro', 'outro', 'logo'];
  //   setSelectedBrandOptions((prev) => (prev.length === allOptions.length ? [] : allOptions));
  // }, []);

  // Clear overlay progress polling and collapse transcript when Brandkit modal closes
  useEffect(() => {
    if (!isBrandKitOpen) {
      if (progressPollingRef.current) {
        clearInterval(progressPollingRef.current);
        progressPollingRef.current = null;
      }
      setExpandedTranscriptTimestamp(null);
      setIsSegmentTranscriptPoppedOut(false);
    }
  }, [isBrandKitOpen]);

  // When Brandkit modal is open and we have segment URLs, request all 3 aspect-ratio endpoints (same as Projects page)
  useEffect(() => {
    if (!isBrandKitOpen || !selectedEdit) return;
    const urls = [
      segmentPreviewUrlsByFormat.original,
      segmentPreviewUrlsByFormat.portrait,
      segmentPreviewUrlsByFormat.square,
    ].filter((u): u is string => Boolean(u));
    if (urls.length === 0) return;
    const controller = new AbortController();
    urls.forEach((url) => {
      fetch(url, {
        method: 'GET',
        headers: { Range: 'bytes=0-0' },
        signal: controller.signal,
      }).catch(() => undefined);
    });
    return () => controller.abort();
  }, [
    isBrandKitOpen,
    selectedEdit,
    segmentPreviewUrlsByFormat.original,
    segmentPreviewUrlsByFormat.portrait,
    segmentPreviewUrlsByFormat.square,
  ]);

  // Build a sanitized stream URL from project.streamPath (strip query/hash)
  const sanitizedStreamUrl = useMemo(() => {
    try {
      const raw = project?.streamPath;
      if (!raw) return '';
      const u = new URL(raw);
      return `${u.origin}${u.pathname}`;
    } catch (e) {
      return project?.streamPath?.split('?')[0] ?? '';
    }
  }, [project?.streamPath]);

  // Use refs to store current callbacks to avoid stale closures
  const onNewFragmentsRef = useRef(EXTERNAL_CALLBACK_onNewFragments);
  const onFragmentStatusRef = useRef(EXTERNAL_CALLBACK_onFragmentStatus);

  // Update refs when callbacks change
  onNewFragmentsRef.current = EXTERNAL_CALLBACK_onNewFragments;
  onFragmentStatusRef.current = EXTERNAL_CALLBACK_onFragmentStatus;

  const { setWorkerStatus, setWorkerId, setFragments, setEdits } = useContext(WorkbenchContext);
  const { socketPromise } = useSocketIo();

  // Stream URL setup + full teardown — only re-runs when the stream actually changes.
  // Keeping setFragments([]) here (not in the edits effect below) prevents fragments from
  // being wiped on every project refetch.
  useEffect(() => {
    if (sanitizedStreamUrl) {
      const key = new URL(sanitizedStreamUrl).pathname.substring(1);
      setS3Key(key);
      setVideoUrl(sanitizedStreamUrl);
      console.log('[Workbench] Video URL set:', sanitizedStreamUrl);
    } else {
      console.log('[Workbench] Video URL empty - project.streamPath:', project?.streamPath);
    }

    return () => {
      handleStop();
      setFragments([]);
      setEdits([]);
      setTranscriptItems([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedStreamUrl]);

  // Keep edits in sync whenever project data refreshes (e.g. after export or sidebar open).
  // No teardown here — fragments must not be cleared on every refetch.
  useEffect(() => {
    if (project?.edits) {
      // Filter segments if user has role "user"
      if (isUserRole && assignedSegmentsData?.segmentIds) {
        const assignedSegmentIds = new Set(assignedSegmentsData.segmentIds);
        const filteredEdits = project.edits.filter((edit) => assignedSegmentIds.has(edit.id));
        setEdits(filteredEdits);
      } else {
        setEdits(project.edits);
      }
    }
  }, [project, isUserRole, assignedSegmentsData, setEdits]);

  // Speakers: feed the transcript hook's items into Workbench's transcriptItems
  // state (the slot editors populate from socket transcript_response events).
  // Times are already 0-normalized by transcript-for-speaker, so transcript
  // selection → "New Clip" works against the segment-as-master timeline.
  useEffect(() => {
    if (!isUserRole) return;
    if (!speakerTranscript?.items) return;
    setTranscriptItems(
      speakerTranscript.items
        .map((w) => ({
          ...w,
          text: (w as { text?: string; punctuated_word?: string; word?: string }).text ?? '',
        }))
        .sort((a, b) => a.start - b.start),
    );
  }, [isUserRole, speakerTranscript]);

  // Speakers: editor flow sets WorkbenchContext's `workerId` inside the
  // socket-attach effect (which we skip for speakers). Without it, the
  // create-fragment HTTP call from transcript-selection clip creation
  // bails with "Worker not connected". The streamId is the workerId in
  // streamcontroller's fragments table, so propagate it directly.
  useEffect(() => {
    if (!isUserRole) return;
    if (!project?.streamId) return;
    setWorkerId(project.streamId);
  }, [isUserRole, project?.streamId, setWorkerId]);

  // Speakers: fetch the real PG fragments for this stream so the segment
  // row's actual `renderStatus` (NEW / SCHEDULED / RENDERED / etc.) drives
  // the Create vs. Export button in ClipGrid, and any clips already
  // created (by editors or by this speaker on a previous visit) are
  // included. Editors don't use this hook — their fragments arrive via
  // socket — so the call is gated on speakerWorkerId being non-empty.
  const speakerWorkerId = isUserRole ? project?.streamId ?? '' : '';
  const { data: speakerWorkerFragments } = useGetFragments(
    speakerWorkerId,
    isUserRole ? auth0User?.sub : undefined,
  );

  // The PG segment's true start time (in seconds), used as the offset
  // for clip-create payloads so speaker-created clips are persisted on
  // the full-stream timeline. Falls back to 0 (and to DDB cut start when
  // the PG fragment can't be matched).
  const speakerSegmentFullStreamStartSec = useMemo<number>(() => {
    if (!isUserRole) return 0;
    const seg = assignedSegmentsData?.segments[0];
    if (!seg || !speakerWorkerFragments) return seg?.startTime ?? 0;
    const matchById = seg.fragmentId
      ? speakerWorkerFragments.find(
          (f) => f.type === 'segment' && String(f.id) === String(seg.fragmentId),
        )
      : undefined;
    const matchByName =
      matchById ??
      (seg.name
        ? speakerWorkerFragments.find(
            (f) =>
              f.type === 'segment' &&
              (f.fragmentName ?? '').trim().toLowerCase() === (seg.name ?? '').trim().toLowerCase(),
          )
        : undefined);
    const match = matchById ?? matchByName;
    if (!match) return seg.startTime ?? 0;
    // PG segments store time in centiseconds; convert to seconds.
    return (match.timeStartAdjusted ?? 0) / 100;
  }, [isUserRole, assignedSegmentsData, speakerWorkerFragments]);

  // Speakers: anchor the view on the speaker's PG segment fragment, then
  // filter clips to those overlapping that fragment's true full-stream
  // window. We can't use the DDB cut times for this — they can drift from
  // the PG fragment's actual times — so look the fragment up by
  // `fragmentId` (DDB→PG link) with a `fragmentName` fallback. After we
  // know the PG segment's window, normalize every kept fragment to a
  // 0-based timeline by subtracting the segment's `timeStartAdjusted`
  // (in centiseconds). AVDisplay/ClipGrid render unchanged because the
  // fragment shape is identical to what useFragments produces for editors
  // — only the time origin differs.
  //
  // Conversions mirror useFragments' apiFragments -> internal-state
  // transform: clip times come out of streamcontroller in seconds,
  // segment times in centiseconds; Workbench state is centiseconds
  // throughout.
  //
  // The segment row is forced `isLocked: true` so useRegions disables the
  // resize/drag grabber handles (speakers can play and clip from it but
  // can't edit its boundaries). Clip rows keep their stored lock state.
  useEffect(() => {
    if (!isUserRole) return;
    const seg = assignedSegmentsData?.segments[0];
    if (!seg) return;
    const cutDurationSec = Math.max(0, (seg.endTime ?? 0) - (seg.startTime ?? 0));
    // Prefer the <audio> probe — it reports the video player's duration
    // and matches what the user sees in playback. Skip wavesurfer's 300
    // (the seeded dummy value) so we don't lock the segment row at 5:00
    // when wavesurfer's decode hasn't overridden options.duration yet.
    const fromWavesurfer =
      waveReportedDurationSec && waveReportedDurationSec > 0 && waveReportedDurationSec !== 300
        ? waveReportedDurationSec
        : null;
    const speakerDurationSec = probedSegmentDurationSec ?? fromWavesurfer;
    const durationSec = speakerDurationSec ?? cutDurationSec;
    setLastSegmentEndTimeMs(durationSec * 1000);

    if (!speakerWorkerFragments) {
      // Don't synthesize a placeholder — the renderStatus would be wrong
      // and the speaker would see "Create" on a fragment that's already
      // SCHEDULED upstream. Render an empty list until the PG data lands.
      setFragments([]);
      return;
    }

    const toCs = (frag: ApiFragment, value: number | null | undefined): number | null => {
      if (value == null) return null;
      return frag.type === 'clip' ? Math.round(value * 100) : value;
    };

    // Find the PG segment fragment that backs the speaker's DDB segment.
    // Prefer the fragmentId pointer; fall back to a case-insensitive name
    // match so this still works for older mappings without fragmentId set.
    const matchByFragmentId = seg.fragmentId
      ? speakerWorkerFragments.find(
          (f) => f.type === 'segment' && String(f.id) === String(seg.fragmentId),
        )
      : undefined;
    const matchByName =
      matchByFragmentId ??
      (seg.name
        ? speakerWorkerFragments.find(
            (f) =>
              f.type === 'segment' &&
              (f.fragmentName ?? '').trim().toLowerCase() === (seg.name ?? '').trim().toLowerCase(),
          )
        : undefined);
    const segmentFragment = matchByFragmentId ?? matchByName;

    if (!segmentFragment) {
      console.warn(
        '[speaker workbench] PG segment fragment not found by id or name for assigned segment',
        seg,
      );
      setFragments([]);
      return;
    }

    const segStartAdjCs = toCs(segmentFragment, segmentFragment.timeStartAdjusted) ?? 0;

    const normalized: ApiFragment[] = speakerWorkerFragments
      .map((frag) => {
        const startCs = toCs(frag, frag.timeStart) ?? 0;
        const endCs = toCs(frag, frag.timeEnd ?? null);
        const startAdjCs = toCs(frag, frag.timeStartAdjusted) ?? startCs;
        const endAdjCs = toCs(frag, frag.timeEndAdjusted ?? null) ?? endCs;
        return { frag, startCs, endCs, startAdjCs, endAdjCs };
      })
      // Segments tab: keep only the speaker's own segment row. Clips:
      // pass every clip on the stream through so speakers see the full
      // clip catalog (their own + editors'). Topics are hidden from
      // speakers entirely — there's no Topics tab in their UI and no
      // topic regions should render on the waveform.
      .filter(
        (row) =>
          row.frag.id === segmentFragment.id ||
          row.frag.type === 'clip' ||
          row.frag.type === 'companion',
      )
      .map((row) => {
        const localStart = Math.max(0, row.startCs - segStartAdjCs);
        const localEnd = row.endCs != null ? Math.max(localStart, row.endCs - segStartAdjCs) : null;
        const localStartAdj = Math.max(0, row.startAdjCs - segStartAdjCs);
        const localEndAdj =
          row.endAdjCs != null ? Math.max(localStartAdj, row.endAdjCs - segStartAdjCs) : null;
        const isSegmentRow = row.frag.type === 'segment';
        // For the speaker's segment row, the PG fragment's timeEndAdjusted
        // can extend well past the rendered MP4's actual length. Anchor
        // the displayed end on the probed playback duration so the
        // segment row's time-code matches what plays back.
        const probedEndCs =
          isSegmentRow && speakerDurationSec ? Math.round(speakerDurationSec * 100) : null;
        return {
          ...row.frag,
          timeStart: localStart,
          timeEnd: probedEndCs ?? localEnd,
          timeStartAdjusted: localStartAdj,
          timeEndAdjusted: probedEndCs ?? localEndAdj,
          // Speakers are read-only on every region: no resize/drag handles
          // on the segment row, and no handles on clip rows either, so they
          // can't shift a clip's start/end on the waveform.
          isLocked: true,
        };
      });

    setFragments(normalized);
  }, [
    isUserRole,
    assignedSegmentsData,
    probedSegmentDurationSec,
    waveReportedDurationSec,
    speakerWorkerFragments,
    speakerWaveReadyTick,
    setFragments,
  ]);

  useEffect(() => {
    // Speakers must not subscribe to the worker stream — it would broadcast
    // peak_data / fragment events for the full show, and we'd render the
    // entire stream's waveform instead of just the speaker's segment.
    if (isUserRole) return;
    if (s3Key && project?.streamId && socketPromise) {
      let isSetup = false;

      const removeSocketListeners = (): void => {
        socketPromise.off('worker_status');
        socketPromise.off('fragment_updated');
        socketPromise.off('fragment_status');
        socketPromise.off('transcript_response');
      };

      const setupSocketHandlers = (): void => {
        // Prevent duplicate setup from both immediate call and 'connect' event
        if (isSetup) return;
        isSetup = true;

        // Remove any previously registered listeners before adding new ones
        removeSocketListeners();

        setWorkerId(project.streamId as string);

        socketPromise.on('worker_status', (wsdata: any) => {
          const ws: WorkerStatus = {
            readerRunning: wsdata['readerTaskStatus'] === 'RUNNING',
            analyzerRunning: wsdata['analyzeTaskStatus'] === 'RUNNING',
            bytesRead: wsdata?.bytesRead,
            cutsDetected: wsdata?.cutsDetected,
            lastSeen: new Date().toISOString(),
            loading: false,
          };
          setWorkerStatus(ws);
        });

        socketPromise.on('fragment_updated', (data: any) => {
          onNewFragmentsRef.current(Number(data.sequenceNumber), project.streamId as string);
        });

        socketPromise.on('fragment_status', (data: string) => {
          const fragmentStatus: FragmentStatus = JSON.parse(data);
          onFragmentStatusRef.current(fragmentStatus);
        });

        socketPromise.on('transcript_response', (data: any) => {
          setTranscriptItems((prev) => {
            const incoming: TranscriptItemModel[] = data.words.map((word: any) => ({
              ...word,
              text: word.punctuated_word || word.word,
            }));

            if (!prev.length) {
              return incoming.slice().sort((a, b) => a.start - b.start);
            }

            // Build a set of existing words using a composite key (start + text)
            // for O(1) duplicate detection. Using start alone could incorrectly
            // filter out legitimate words that share the same start time
            // (e.g., overlapping speech, rounding, simultaneous speakers).
            const existingKeys = new Set(prev.map((w) => `${w.start}|${w.text}`));

            // Filter out any incoming words that already exist in prev.
            const newWords = incoming.filter((w) => !existingKeys.has(`${w.start}|${w.text}`));
            if (newWords.length === 0) return prev; // nothing new — skip state update

            // Merge prev + newWords in O(n + m), both sorted by start time.
            const sorted = newWords.slice().sort((a, b) => a.start - b.start);
            const merged: TranscriptItemModel[] = [];
            let i = 0;
            let j = 0;

            while (i < prev.length && j < sorted.length) {
              if (prev[i].start <= sorted[j].start) {
                merged.push(prev[i]);
                i += 1;
              } else {
                merged.push(sorted[j]);
                j += 1;
              }
            }

            if (i < prev.length) {
              merged.push(...prev.slice(i));
            }
            if (j < sorted.length) {
              merged.push(...sorted.slice(j));
            }

            return merged;
          });
        });

        socketPromise.emit('worker_start', { projectId, streamId: project.streamId });
      };

      // Handle reconnections: re-setup listeners when socket reconnects
      const handleReconnect = (): void => {
        isSetup = false;
        setupSocketHandlers();
      };

      if (socketPromise.connected) {
        setupSocketHandlers();
      } else {
        socketPromise.on('connect', setupSocketHandlers);
      }

      // Listen for reconnect to re-register handlers cleanly
      socketPromise.io.on('reconnect', handleReconnect);

      return () => {
        socketPromise.emit('worker_end', { projectId, streamId: project.streamId, key: s3Key });
        removeSocketListeners();
        socketPromise.off('connect', setupSocketHandlers);
        socketPromise.io.off('reconnect', handleReconnect);
      };
    }
  }, [s3Key, socketPromise, isUserRole]);

  // Update lastSegmentEndTimeMs when peakDataProcessor gets new data
  useEffect(() => {
    // Set initial value
    const initialValue = peakDataProcessor.getLastSegmentEndTimeMs();
    setLastSegmentEndTimeMs(initialValue);

    // Set up real-time callback for updates
    const handleEndTimeUpdate = (endTimeMs: number): void => {
      setLastSegmentEndTimeMs(endTimeMs);
    };

    peakDataProcessor.setOnEndTimeUpdateCallback(handleEndTimeUpdate);

    return () => {
      peakDataProcessor.clearOnEndTimeUpdateCallback();
    };
  }, [peakDataProcessor]);

  return (
    <LastSegmentEndTimeProvider value={lastSegmentEndTimeMs}>
      <>
        {isLoading || !project ? (
          <Loading />
        ) : (
          <div
            className={`${styles.workbenchWrapper} ${
              isCustomizeMode ? styles.workbenchWrapperCustomize : ''
            }`}
          >
            <div className={styles.workbenchContainer}>
              <div className={styles.workbenchContent}>
                <header
                  className={`${styles.workbenchHeader} ${
                    isCustomizeMode ? styles.workbenchHeaderCustomize : ''
                  }`}
                >
                  <div className={styles.headerLeft}>
                    <Link to={PROJECTS_URL} className={styles.backButton}>
                      <img src={backArrowIcon} alt="Back" />
                    </Link>
                    <div className={styles.projectInfo}>
                      <span className={styles.projectName}>
                        {project.name}
                        {formattedProjectDate && (
                          <span className={styles.projectDate}>{' -' + formattedProjectDate}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className={styles.headerRight}>
                    {isCustomizeMode ? (
                      <button className={styles.cancelButton} onClick={handleCustomizeSave}>
                        Save
                      </button>
                    ) : (
                      <>
                        <button className={styles.headerButton} onClick={handleCustomizeClick}>
                          <img src={customizeIcon} alt="Customize" />
                          <span>Customize</span>
                        </button>
                        {isEditorRole && (
                          <button
                            className={styles.headerButton}
                            onClick={handleToggleRunsheetSelector}
                          >
                            <img src={runsheetIcon} alt="Runsheet" />
                            <span>Runsheet</span>
                          </button>
                        )}
                        <button
                          className={styles.headerButton}
                          onClick={handleQuoteDetection}
                          disabled={isQuoteDetecting || !project.streamId}
                          style={{ opacity: isQuoteDetecting ? 0.5 : 1 }}
                        >
                          <img src={quoteIcon} alt="Quote Detection" />
                          <span>{isQuoteDetecting ? 'Detecting...' : 'Quote Detection'}</span>
                        </button>
                        <button
                          className={styles.headerButton}
                          onClick={() => setIsGenerateSegmentsOpen(true)}
                          disabled={isGeneratingSegments || !project.streamId}
                          style={{ opacity: isGeneratingSegments ? 0.5 : 1 }}
                        >
                          <img src={segmentsIcon} alt="Generate Segments" />
                          <span>
                            {isGeneratingSegments ? 'Generating...' : 'Generate Segments'}
                          </span>
                        </button>
                        {showRunsheetSelector && project.streamId && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              mt: 1,
                              zIndex: 1000,
                              backgroundColor: 'white',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              borderRadius: '4px',
                              padding: '8px',
                            }}
                          >
                            <RunsheetSelector
                              streamId={project.streamId}
                              onRunsheetChanged={handleRunsheetChanged}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </div>
                </header>
                <GenerateSegmentsModal
                  open={isGenerateSegmentsOpen}
                  isSubmitting={isGeneratingSegments}
                  handleClose={() => setIsGenerateSegmentsOpen(false)}
                  handleSubmit={handleGenerateSegments}
                />
                {isBrandKitOpen && (
                  <div className={styles.brandKitOverlay}>
                    <div className={styles.brandKitModal}>
                      <div className={styles.brandKitHeaderRow}>
                        <span className={styles.brandKitTitle}>
                          {'Export '}
                          {selectedFragmentType === 'clip'
                            ? 'Clip'
                            : selectedFragmentType === 'topic'
                            ? 'Topic'
                            : 'Segment'}
                        </span>
                        <button
                          type="button"
                          className={styles.brandKitCloseButton}
                          onClick={() => setIsBrandKitOpen(false)}
                          aria-label="Close brand kit"
                        >
                          ×
                        </button>
                      </div>
                      <div className={styles.brandKitBody}>
                        <label className={styles.brandKitField}>
                          <span className={styles.brandKitFieldLabel}>
                            {selectedFragmentType === 'clip'
                              ? 'Clip'
                              : selectedFragmentType === 'topic'
                              ? 'Topic'
                              : 'Segment'}{' '}
                            Name
                          </span>
                          <input
                            type="text"
                            className={styles.brandKitTextInput}
                            placeholder="Speaker Name"
                            value={segmentName}
                            onChange={(event) => setSegmentName(event.target.value)}
                          />
                        </label>

                        <div className={styles.brandKitSection}>
                          <div className={styles.brandKitSectionTitle}>Aspect Ratio</div>
                          {!isReframingEnabled && (
                            <div className={styles.brandKitAspectRatioHint}>
                              Portrait/Square available for segments under 5 minutes
                            </div>
                          )}
                          {isReframingEnabled &&
                            (segmentPreviewUrlsByFormat.portrait ||
                              segmentPreviewUrlsByFormat.square) &&
                            (!isPortraitAvailable || !isSquareAvailable) && (
                              <div className={styles.brandKitAspectRatioHint}>
                                Processing reframed versions...
                              </div>
                            )}
                          <div className={styles.brandKitAspectRatioList}>
                            {(
                              [
                                {
                                  ratio: '16:9' as const,
                                  icon: 'monitor',
                                  ariaLabel: '16:9 widescreen',
                                },
                                {
                                  ratio: '4:3' as const,
                                  icon: 'phone',
                                  ariaLabel: '9:16 portrait',
                                },
                                { ratio: '1:1' as const, icon: 'square', ariaLabel: '1:1 square' },
                              ] as const
                            ).map(({ ratio, icon, ariaLabel }) => {
                              const isRatioEnabled =
                                ratio === '16:9'
                                  ? is16x9Enabled
                                  : ratio === '4:3'
                                  ? isPortraitAvailable
                                  : isSquareAvailable;
                              return (
                                <button
                                  key={ratio}
                                  type="button"
                                  className={`${styles.brandKitAspectButton} ${
                                    selectedAspectRatio === ratio
                                      ? styles.brandKitAspectButtonActive
                                      : ''
                                  } ${!isRatioEnabled ? styles.brandKitAspectButtonDisabled : ''}`}
                                  onClick={() => isRatioEnabled && setSelectedAspectRatio(ratio)}
                                  disabled={!isRatioEnabled}
                                  aria-label={ariaLabel}
                                >
                                  <span className={styles.brandKitAspectIcon}>
                                    {icon === 'monitor' && (
                                      <svg
                                        width="20"
                                        height="16"
                                        viewBox="0 0 24 20"
                                        fill="currentColor"
                                        aria-hidden
                                      >
                                        <rect
                                          x="1"
                                          y="1"
                                          width="22"
                                          height="14"
                                          rx="1"
                                          stroke="currentColor"
                                          fill="none"
                                          strokeWidth="1.5"
                                        />
                                        <path d="M8 18h8" stroke="currentColor" strokeWidth="1.5" />
                                      </svg>
                                    )}
                                    {icon === 'phone' && (
                                      <svg
                                        width="12"
                                        height="20"
                                        viewBox="0 0 12 20"
                                        fill="none"
                                        aria-hidden
                                      >
                                        <rect
                                          x="1"
                                          y="1"
                                          width="10"
                                          height="18"
                                          rx="2"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                        />
                                      </svg>
                                    )}
                                    {icon === 'square' && (
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        aria-hidden
                                      >
                                        <rect
                                          x="1"
                                          y="1"
                                          width="16"
                                          height="16"
                                          rx="1"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                        />
                                      </svg>
                                    )}
                                  </span>
                                  <span>{ratio === '4:3' ? '9:16' : ratio}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className={styles.brandKitSection}>
                          <span className={styles.brandKitSectionTitle}>Branding Options</span>
                          <div className={styles.brandKitOptionsGrid}>
                            {[
                              { key: 'intro', label: 'Intro' },
                              { key: 'outro', label: 'Outro' },
                              { key: 'logo', label: 'Logo Watermark' },
                            ].map((option) => (
                              <button
                                key={option.key}
                                type="button"
                                className={`${styles.brandKitOptionButton} ${
                                  selectedBrandOptions.includes(option.key as BrandOptionKey)
                                    ? styles.brandKitOptionButtonActive
                                    : ''
                                }`}
                                onClick={() => toggleBrandOption(option.key as BrandOptionKey)}
                              >
                                {option.label}
                              </button>
                            ))}
                            {selectedBrandOptions.includes('logo') && (
                              <select
                                className={styles.brandKitLogoPositionSelect}
                                value={logoPosition}
                                onChange={(e) => setLogoPosition(e.target.value)}
                              >
                                <option value="top-right">Top Right</option>
                                <option value="top-left">Top Left</option>
                                <option value="bottom-left">Bottom Left</option>
                                <option value="bottom-right">Bottom Right</option>
                              </select>
                            )}
                          </div>
                        </div>

                        <div className={styles.brandKitPreviewSection}>
                          <div className={styles.brandKitPreviewHeader}>
                            <span className={styles.brandKitSectionTitle}>Preview</span>
                            <span className={styles.brandKitPreviewAspectLabel}>
                              {selectedAspectRatio === '4:3' ? '9:16' : selectedAspectRatio}
                            </span>
                          </div>
                          <div className={styles.brandKitPreviewFrame}>
                            <div
                              className={styles.brandKitPreviewFrameInner}
                              style={
                                selectedAspectRatio === '4:3'
                                  ? { aspectRatio: '9/16' }
                                  : selectedAspectRatio === '1:1'
                                  ? { aspectRatio: '1/1' }
                                  : { aspectRatio: '16/9' }
                              }
                            >
                              {isBrandKitLoading &&
                              !effectivePreviewUrl &&
                              selectedSegmentId &&
                              isSelectedSegmentProcessed ? (
                                <Loading />
                              ) : effectivePreviewUrl ? (
                                <>
                                  <div className={styles.brandKitPreviewVideoWrap}>
                                    {isHlsPreview ? (
                                      <SimpleHlsPlayer
                                        src={effectivePreviewUrl}
                                        startSec={hlsPreviewStartTimeSec}
                                        endSec={hlsPreviewEndTimeSec}
                                      />
                                    ) : (
                                      <BrandKitVideoItem path={effectivePreviewUrl} controls />
                                    )}
                                    {!isBrandedPreview && (
                                      <>
                                        {selectedBrandOptions.includes('logo') &&
                                          effectiveBrandKitAssets.logo.path && (
                                            <div
                                              className={styles.brandKitPreviewLogo}
                                              style={{
                                                top: logoPosition.startsWith('top-')
                                                  ? 8
                                                  : undefined,
                                                bottom: logoPosition.startsWith('bottom-')
                                                  ? 8
                                                  : undefined,
                                                left: logoPosition.endsWith('-left')
                                                  ? 8
                                                  : undefined,
                                                right: logoPosition.endsWith('-right')
                                                  ? 8
                                                  : undefined,
                                              }}
                                            >
                                              <BrandKitVideoItem
                                                path={effectiveBrandKitAssets.logo.path}
                                                style={{
                                                  maxWidth: 80,
                                                  maxHeight: 40,
                                                  objectFit: 'contain',
                                                }}
                                              />
                                            </div>
                                          )}
                                      </>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className={styles.brandKitPreviewPlaceholder}>
                                  {selectedSegmentId
                                    ? 'Please wait, or refresh browser to view'
                                    : 'Select a segment in the timeline'}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={styles.brandKitTimelineBar}>
                            <span className={styles.brandKitTimelineSegment}>Intro(3s)</span>
                            <span
                              className={`${styles.brandKitTimelineSegment} ${styles.brandKitTimelineSegmentContent}`}
                            >
                              Content({contentDurationSec}s)
                            </span>
                            <span className={styles.brandKitTimelineSegment}>Outro(3s)</span>
                          </div>
                          <div className={styles.brandKitMediaUploadSection}>
                            <div className={styles.brandKitAssetGrid}>
                              {(
                                [
                                  { key: 'logo', label: 'Logo', accept: ['.png'] },
                                  { key: 'intro', label: 'Intro Video', accept: ['.png', '.mp4'] },
                                  { key: 'outro', label: 'Outro Video', accept: ['.png', '.mp4'] },
                                ] as const
                              ).map((item) => (
                                <div key={item.key} className={styles.brandKitAssetCard}>
                                  <div className={styles.brandKitAssetHeader}>
                                    <span className={styles.brandKitAssetLabel}>{item.label}</span>
                                  </div>

                                  <div className={styles.brandKitAssetPreviewBox}>
                                    {effectiveBrandKitAssets[item.key].path ? (
                                      <div className={styles.brandKitAssetMediaWrapper}>
                                        <BrandKitVideoItem
                                          path={effectiveBrandKitAssets[item.key].path ?? ''}
                                          controls
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <div className={styles.brandKitAssetPlaceholder}>
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="#94a3b8"
                                          strokeWidth="2"
                                        >
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                          <circle cx="8.5" cy="8.5" r="1.5" />
                                          <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                        <span>{`No ${item.key}`}</span>
                                      </div>
                                    )}
                                  </div>

                                  <FileUpload
                                    styleEl="button"
                                    fileTypes={item.accept as unknown as string[]}
                                    onChange={(file: File) =>
                                      void handleBrandKitUpload(item.key as BrandKitAssets, file)
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      {(!selectedSegmentId || !isSelectedSegmentProcessed) && (
                        <div className={styles.brandKitHelperMessage}>
                          {!selectedSegmentId
                            ? 'Select a segment in the timeline.'
                            : selectedAspectRatio === '4:3'
                            ? 'Portrait (9:16) video is not ready yet. Please wait for reframing to complete.'
                            : selectedAspectRatio === '1:1'
                            ? 'Square (1:1) video is not ready yet. Please wait for reframing to complete.'
                            : 'This segment is still processing. Only processed segments can be exported with branding.'}
                        </div>
                      )}
                      <div className={styles.brandKitFooter}>
                        <button
                          type="button"
                          className={styles.brandKitCancelButton}
                          onClick={() => setIsBrandKitOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={styles.brandKitExportButton}
                          disabled={
                            !effectiveSegmentName ||
                            !selectedSegmentId ||
                            !isSelectedSegmentProcessed ||
                            isExporting
                          }
                          onClick={() => {
                            if (
                              !effectiveSegmentName ||
                              !selectedSegmentId ||
                              !isSelectedSegmentProcessed ||
                              isExporting
                            )
                              return;
                            void handleExportClipDirect();
                          }}
                        >
                          {isExporting ? (
                            'Exporting…'
                          ) : (
                            <>
                              <svg
                                className={styles.brandKitExportIcon}
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              {'Export '}
                              {selectedFragmentType === 'clip'
                                ? 'Clip'
                                : selectedFragmentType === 'topic'
                                ? 'Topic'
                                : 'Segment'}
                            </>
                          )}
                        </button>
                      </div>
                      {exportError && <div className={styles.brandKitErrorText}>{exportError}</div>}

                      <div className={styles.brandKitExportsSection}>
                        <div className={styles.brandKitSectionTitle}>Version History</div>
                        <div className={styles.brandKitExportsList}>
                          {!selectedEdit?.overlayHistory ||
                          selectedEdit.overlayHistory.length === 0 ? (
                            <div className={styles.brandKitNoExports}>
                              No version history yet. Export a segment or clip with branding to see
                              your exported segments or clips here.
                            </div>
                          ) : (
                            <List dense disablePadding sx={{ width: '100%' }}>
                              {(
                                selectedEdit.overlayHistory as
                                  | {
                                      overlayPath: string;
                                      format: string;
                                      timestamp: number;
                                      status?: string;
                                    }[]
                                  | undefined
                              )
                                ?.slice()
                                .reverse()
                                .map((overlay, index) => (
                                  <Box
                                    key={overlay.timestamp}
                                    sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}
                                  >
                                    <ListItem
                                      className={styles.brandKitOverlayHistoryItem}
                                      sx={{
                                        bgcolor: 'white',
                                        borderRadius: 1,
                                        border:
                                          expandedTranscriptTimestamp === overlay.timestamp
                                            ? '1px solid #f1b400'
                                            : '1px solid #e8e8e8',
                                        flexWrap: 'wrap',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: '#fafafa' },
                                      }}
                                      onClick={() =>
                                        setExpandedTranscriptTimestamp((prev) =>
                                          prev === overlay.timestamp ? null : overlay.timestamp,
                                        )
                                      }
                                      secondaryAction={
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            flexShrink: 0,
                                            pl: 2,
                                          }}
                                        >
                                          <button
                                            type="button"
                                            title="View transcript"
                                            className={styles.brandKitTranscriptToggle}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedTranscriptTimestamp((prev) =>
                                                prev === overlay.timestamp
                                                  ? null
                                                  : overlay.timestamp,
                                              );
                                            }}
                                          >
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke={
                                                expandedTranscriptTimestamp === overlay.timestamp
                                                  ? '#f1b400'
                                                  : 'currentColor'
                                              }
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14 2 14 8 20 8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10 9 9 9 8 9" />
                                            </svg>
                                          </button>
                                          {isEditorRole && (
                                            <button
                                              type="button"
                                              title="Send to speaker(s)"
                                              aria-label="Send to speakers"
                                              className={styles.brandKitTranscriptToggle}
                                              disabled={
                                                pendingInviteTimestamp === overlay.timestamp
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                void handleSendSpeakerInvites(overlay.timestamp);
                                              }}
                                            >
                                              {pendingInviteTimestamp === overlay.timestamp ? (
                                                <CircularProgress size={14} thickness={5} />
                                              ) : (
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                >
                                                  <line x1="22" y1="2" x2="11" y2="13" />
                                                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                                </svg>
                                              )}
                                            </button>
                                          )}
                                          <PollingDownloadButton
                                            url={
                                              overlay.overlayPath.startsWith('http')
                                                ? overlay.overlayPath
                                                : (() => {
                                                    try {
                                                      return convertS3PathToHttpUrl(
                                                        overlay.overlayPath,
                                                      );
                                                    } catch {
                                                      return overlay.overlayPath;
                                                    }
                                                  })()
                                            }
                                            fileName={getOverlayFileName(overlay)}
                                            iconOnly
                                          />
                                        </Box>
                                      }
                                    >
                                      <ListItemText
                                        primary={`${index === 0 ? '(Latest) ' : ''}${
                                          overlay.format.charAt(0).toUpperCase() ?? ''
                                        }${overlay.format.slice(1) ?? ''}`}
                                        secondary={new Date(overlay.timestamp).toLocaleString()}
                                        primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                                        secondaryTypographyProps={{
                                          fontSize: 12,
                                          color: 'text.secondary',
                                        }}
                                        sx={{ flex: '1 1 auto', minWidth: 0 }}
                                      />
                                    </ListItem>
                                    {expandedTranscriptTimestamp === overlay.timestamp && (
                                      <div className={styles.brandKitTranscriptPanel}>
                                        {segmentTranscriptItems.length > 0 ? (
                                          <WorkbenchTranscript
                                            transcriptItems={segmentTranscriptItems}
                                            disableVirtualization
                                            onPopout={() => setIsSegmentTranscriptPoppedOut(true)}
                                          />
                                        ) : (
                                          <div className={styles.brandKitNoTranscript}>
                                            No transcript available for this segment.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Box>
                                ))}
                            </List>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {isSegmentTranscriptPoppedOut && segmentTranscriptItems.length > 0 && (
                  <PopoutWindow
                    title="Segment Transcript"
                    width={800}
                    height={900}
                    onClose={() => setIsSegmentTranscriptPoppedOut(false)}
                  >
                    <Box
                      style={{
                        height: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#fff',
                        padding: '20px',
                      }}
                    >
                      <Box
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 16,
                          paddingBottom: 8,
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <h2 style={{ margin: 0 }}>
                          Segment Transcript
                          {effectiveSegmentName ? ` — ${effectiveSegmentName}` : ''}
                        </h2>
                      </Box>
                      <Box style={{ flex: 1, minHeight: 0 }}>
                        <WorkbenchTranscript
                          transcriptItems={segmentTranscriptItems}
                          disableVirtualization
                        />
                      </Box>
                    </Box>
                  </PopoutWindow>
                )}
                <AVDisplay
                  videoSrc={videoUrl}
                  cutUrl={project.streamPath as string}
                  onReady={onAvComponentReady}
                  transcriptItems={transcriptItems}
                  refetchProject={refetchBrandingSidebar}
                  isCustomizeMode={isCustomizeMode}
                  onSelectedSegmentChange={handleSelectedSegmentChange}
                  onSegmentClipCardClick={() => {
                    setExportError(null);
                    setIsBrandKitOpen(true);
                  }}
                  clipCreateTimeOffsetSec={isUserRole ? speakerSegmentFullStreamStartSec : 0}
                />
              </div>
            </div>
          </div>
        )}
      </>
    </LastSegmentEndTimeProvider>
  );
};

export default Workbench;
